import React, { useRef, useEffect, useState } from 'react';
import { PizzaLocation } from '../types';

interface MapProps {
  locations: PizzaLocation[];
  center: google.maps.LatLngLiteral;
  searchPerformed?: boolean;
}

function MapComponent({ locations, center, searchPerformed = false }: MapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    if (window.google?.maps) {
      setIsMapReady(true);
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current || !searchPerformed || !isMapReady) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center,
        zoom: 13,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        draggable: true,
        zoomControl: true,
        scrollwheel: true,
        keyboardShortcuts: true,
        disableDoubleClickZoom: false,
        gestureHandling: 'cooperative',
        clickableIcons: false
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }
  }, [searchPerformed, center, isMapReady]);

  useEffect(() => {
    if (!searchPerformed || !mapInstanceRef.current || !isMapReady) return;

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const userMarker = new google.maps.Marker({
      position: center,
      map: mapInstanceRef.current,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#3B82F6',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 3,
      }
    });
    markersRef.current.push(userMarker);

    locations.forEach((location) => {
      const marker = new google.maps.Marker({
        position: { lat: location.latitude, lng: location.longitude },
        map: mapInstanceRef.current!,
        icon: {
          url: 'https://cdn-icons-png.flaticon.com/512/661/661740.png',
          scaledSize: new google.maps.Size(48, 48),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(24, 24)
        },
        title: location.name,
        animation: google.maps.Animation.DROP
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 16px; min-width: 200px; max-width: 300px;">
            <h3 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">${location.name}</h3>
            <p style="margin: 8px 0; color: #4B5563; font-size: 14px;">${location.address}</p>
            ${location.rating ? `<p style="margin: 8px 0; font-size: 14px;">Rating: ${location.rating} ⭐</p>` : ''}
            <div style="display: flex; gap: 8px; margin-top: 12px;">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}"
                target="_blank"
                rel="noopener noreferrer"
                style="padding: 8px 12px; background-color: #DC2626; color: white; text-decoration: none; border-radius: 6px; font-size: 14px;"
              >
                Get Directions
              </a>
              ${location.phone ? `
                <a
                  href="tel:${location.phone}"
                  style="padding: 8px 12px; background-color: #059669; color: white; text-decoration: none; border-radius: 6px; font-size: 14px;"
                >
                  Call Now
                </a>
              ` : ''}
            </div>
          </div>
        `
      });

      marker.addListener('click', () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        infoWindow.open(mapInstanceRef.current, marker);
        infoWindowRef.current = infoWindow;
      });

      markersRef.current.push(marker);
    });

    if (locations.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(center);
      locations.forEach(location => {
        bounds.extend({ lat: location.latitude, lng: location.longitude });
      });
      mapInstanceRef.current.fitBounds(bounds);
    } else {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(13);
    }
  }, [locations, center, searchPerformed, isMapReady]);

  return (
    <div className="w-full bg-white shadow-md overflow-hidden relative">
      {!searchPerformed ? (
        <div className="w-full h-[500px] flex items-center justify-center bg-gray-100 overflow-hidden">
          <img 
            src="https://i.imgur.com/WKGmO6L.jpeg" 
            alt="Map preview" 
            className="w-full h-[500px] object-cover object-center"
          />
        </div>
      ) : (
        <div ref={mapRef} className="w-full h-[500px]" />
      )}
      {searchPerformed && locations.length === 0 && !isMapReady && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="bg-white/90 backdrop-blur-sm px-6 py-4 rounded-lg shadow-lg text-center">
            <p className="text-xl font-semibold text-gray-800">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export const Map: React.FC<MapProps> = (props) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return (
      <div className="w-full h-[500px] bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return <MapComponent {...props} />;
};